import { memo, useMemo } from "react"
import { OrderBook_Session, Security, SecurityCategory_CategoryCode } from '../../proto/generated/security'
import { Box } from "@mui/material"
import { getSecurityKey } from "../../util/security"
import { toLocaleStringIn } from "../../util/general"
import OrderBookItem from "../orderBook/OrderBookItem"
import { formatLargeNumber } from "../../util/screener"
import { formatUnixTimestampForOrderBook } from "../../util/dateTime"
import { useOrderBookOverviewTick } from "../../hooks/useSecurityMarketTicks"
import SecurityRange from "../securityInfo/SecurityRange"

export type OrderBookOverviewProps = {
    security: Security
}

const formatPrice = (value: number | undefined, priceMultiplier: number) =>
    value != null ? toLocaleStringIn(value / priceMultiplier) : '-'

const formatQty = (value: number | undefined) =>
    value != null ? formatLargeNumber(value) : '-'

const isAuctionSession = (session: OrderBook_Session) =>
    session === OrderBook_Session.PRE_OPEN
    || session === OrderBook_Session.CALL_AUCTION
    || session === OrderBook_Session.CLOSING_AUCTION

const isIndexIndicativeSession = (session: OrderBook_Session) =>
    session === OrderBook_Session.PRE_OPEN
    || session === OrderBook_Session.CLOSING_AUCTION

const OrderBookOverview: React.FC<OrderBookOverviewProps> = ({ security }) => {
    const securityKey = useMemo(() => security ? getSecurityKey(security.exchange, security.code) : '', [security])
    const orderBook = useOrderBookOverviewTick(securityKey)
    const priceMultiplier = security?.priceMultiplier ?? 100
    const isIndex = security.categoryCode === SecurityCategory_CategoryCode.INDEX

    if (!orderBook) return null

    const showAuction = !isIndex && !!orderBook.auction && isAuctionSession(orderBook.session)
    const showIndexIndicativePrice = isIndex
        && orderBook.auction?.indicativePrice != null
        && isIndexIndicativeSession(orderBook.session)
    const showReferencePrice = orderBook.session === OrderBook_Session.CLOSING_AUCTION

    return (
        <Box display="flex" flexDirection="column" gap={1} width="100%">
            <Box
                display="flex"
                flexDirection="column"
                gap={1}
                width="100%"
                sx={{
                    px: 1,
                    py: 0.75,
                    borderRadius: 1,
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    bgcolor: (theme) => theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.04)'
                        : 'rgba(0,0,0,0.02)',
                }}
            >
                <Box display="flex" flexDirection="row" gap={1} justifyContent="space-between" width="100%">
                    <Box width="50%" minWidth={0}>
                        <OrderBookItem title="Open" value={formatPrice(orderBook.open, priceMultiplier)} />
                        <OrderBookItem title="Low" value={formatPrice(orderBook.low, priceMultiplier)} />
                    </Box>
                    <Box width="50%" minWidth={0}>
                        <OrderBookItem title="Prev. Close" value={formatPrice(orderBook.close, priceMultiplier)} />
                        <OrderBookItem title="High" value={formatPrice(orderBook.high, priceMultiplier)} />
                    </Box>
                </Box>
                <SecurityRange security={security} hideDayLabels />
            </Box>

            {!isIndex && (
                <Box display="flex" flexDirection="row" gap={1} justifyContent="space-between" width="100%">
                    <Box width="50%" minWidth={0}>
                        <OrderBookItem title="Volume" value={formatLargeNumber(orderBook.volumeTradedToday)} />
                        <OrderBookItem title="Lower Circuit" value={formatPrice(orderBook.lowerCircuit, priceMultiplier)} />
                        <OrderBookItem title="LTQ" value={formatQty(orderBook.lastTradedQuantity || undefined)} />
                    </Box>
                    <Box width="50%" minWidth={0}>
                        <OrderBookItem title="Avg. Price" value={formatPrice(orderBook.averageTradedPrice || undefined, priceMultiplier)} />
                        <OrderBookItem title="Upper Circuit" value={formatPrice(orderBook.upperCircuit, priceMultiplier)} />
                        <OrderBookItem
                            title="LTT"
                            value={orderBook.lastTradedTime ? formatUnixTimestampForOrderBook(orderBook.lastTradedTime) : '-'}
                        />
                    </Box>
                </Box>
            )}

            {(orderBook.openInterest != null || orderBook.inav != null) && (
                <Box display="flex" flexDirection="row" gap={1} justifyContent="space-between" width="100%">
                    <Box width="50%" minWidth={0}>
                        {orderBook.openInterest != null ? (
                            <OrderBookItem title="OI" value={formatQty(orderBook.openInterest)} />
                        ) : (
                            <OrderBookItem title="iNAV" value={formatPrice(orderBook.inav, priceMultiplier)} />
                        )}
                    </Box>
                    <Box width="50%" minWidth={0}>
                        {orderBook.openInterest != null && orderBook.inav != null && (
                            <OrderBookItem title="iNAV" value={formatPrice(orderBook.inav, priceMultiplier)} />
                        )}
                    </Box>
                </Box>
            )}

            {showIndexIndicativePrice && (
                <Box display="flex" flexDirection="row" gap={1} width="100%">
                    <Box width="50%" minWidth={0}>
                        <OrderBookItem
                            title="Indicative Price"
                            value={formatPrice(orderBook.auction?.indicativePrice, priceMultiplier)}
                        />
                    </Box>
                </Box>
            )}

            {showAuction && orderBook.auction && (
                <Box display="flex" flexDirection="row" gap={1} justifyContent="space-between" width="100%">
                    <Box width="50%" minWidth={0}>
                        <OrderBookItem
                            title="Indicative Price"
                            value={formatPrice(orderBook.auction.indicativePrice, priceMultiplier)}
                        />
                        <OrderBookItem
                            title="Total Imbalance"
                            value={formatQty(orderBook.auction.totalImbalanceQuantity)}
                        />
                        {showReferencePrice && (
                            <OrderBookItem
                                title="Reference Price"
                                value={formatPrice(orderBook.auction.referencePrice, priceMultiplier)}
                            />
                        )}
                    </Box>
                    <Box width="50%" minWidth={0}>
                        <OrderBookItem
                            title="Indicative Qty"
                            value={formatQty(orderBook.auction.indicativeQuantity)}
                        />
                        <OrderBookItem
                            title="Market Imbalance"
                            value={formatQty(orderBook.auction.marketImbalanceQuantity)}
                        />
                    </Box>
                </Box>
            )}
        </Box>
    )
}

export default memo(OrderBookOverview)
